import { useRef, useState, type ReactNode } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CustomerCreditNoteDetailSchema,
  CustomerCreditNoteSchema,
  CustomerInvoiceDetailSchema,
} from '@taqon/contracts';
import { Link as RouterLink, useNavigate, useParams, useSearchParams } from 'react-router';
import { getData, getPage, newIdempotencyKey, postJson } from '../../shared/api/client';
import { RecordHistory } from '../../shared/audit/AuditTrail';
import { DocumentsPanel } from '../../shared/documents/DocumentsPanel';
import { hasCapability, useSession } from '../../shared/session/session';
import { ConfirmDialog } from '../../shared/ui/confirm';
import { StatusChip } from '../../shared/ui/forms';
import { EmptyState, ErrorState, LoadingState, PageHeader, Section } from '../../shared/ui/page';
import { useUrlList } from '../../shared/ui/url-list';
import { refreshAfterPosting } from '../finance/cache';
import { Facts, JournalRef, RecordFailure, RecordLink } from '../finance/detail';
import {
  businessToday,
  emptyLine,
  lineProblems,
  lineRequests,
  linesComplete,
  LinesEditor,
  type LineDraft,
} from '../finance/line-editor';
import { sum } from '../finance/money';
import { RefusalState } from '../finance/refusal';
import { CUSTOMER_INVOICES } from './CustomerInvoicesPages';
import { SearchFilterBar } from '../../shared/ui/filters';

const CREDIT_NOTES = '/api/v1/finance/customer-credit-notes';
const NOTE = 'finance.customer_credit_note';

/** Customer credit notes (FRD-FIN-060): each against an invoice, filtered in the URL. */
export function CustomerCreditNotesPage() {
  const list = useUrlList({ pageSize: 50 });
  const params = list.apiParams(
    { status: 'status', invoice: 'customer_invoice_id' },
    '-credit_note_date',
  );
  const notes = useQuery({
    queryKey: ['customer-credit-notes', params.toString()],
    queryFn: () => getPage(`${CREDIT_NOTES}?${params.toString()}`, CustomerCreditNoteSchema),
  });
  return (
    <Stack spacing={3}>
      <PageHeader
        title="Customer credit notes"
        description="Credits against issued invoices; a new one starts from the invoice it credits."
      />
      <Box role="search" aria-label="Filter the credit notes">
        <SearchFilterBar>
          <TextField
            select
            size="small"
            label="Status"
            value={list.get('status')}
            sx={{ minWidth: 160 }}
            onChange={(e) => {
              list.set('status', e.target.value);
            }}
          >
            <MenuItem value="">Any</MenuItem>
            <MenuItem value="draft">Draft</MenuItem>
            <MenuItem value="issued">Issued</MenuItem>
          </TextField>
        </SearchFilterBar>
      </Box>
      <Section>
        {notes.isPending && <LoadingState label="Loading credit notes" />}
        {notes.isError && <ErrorState message={notes.error.message} />}
        {notes.data?.items.length === 0 && (
          <EmptyState
            title="No credit notes"
            detail="Open an issued invoice and choose Credit note."
          />
        )}
        {notes.data !== undefined && notes.data.items.length > 0 && (
          <>
            <Table size="small" aria-label="Customer credit notes">
              <TableHead>
                <TableRow>
                  <TableCell>Credit note</TableCell>
                  <TableCell>Invoice</TableCell>
                  <TableCell>Client</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {notes.data.items.map((note) => (
                  <TableRow key={note.id} hover>
                    <TableCell>
                      <RecordLink to={`/customer-credit-notes/${note.id}`}>
                        {note.documentNo ?? `Draft #${note.id}`}
                      </RecordLink>
                    </TableCell>
                    <TableCell>
                      <RecordLink to={`/customer-invoices/${note.customerInvoiceId}`}>
                        {note.invoiceDocumentNo ?? `#${note.customerInvoiceId}`}
                      </RecordLink>
                    </TableCell>
                    <TableCell>{note.clientLabel ?? '—'}</TableCell>
                    <TableCell>{note.creditNoteDate}</TableCell>
                    <TableCell align="right">{note.grossTotal}</TableCell>
                    <TableCell>
                      <StatusChip status={note.status} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={notes.data.total}
              page={list.page}
              rowsPerPage={list.pageSize}
              rowsPerPageOptions={[25, 50, 100]}
              onPageChange={(_, next) => {
                list.setPage(next);
              }}
              onRowsPerPageChange={(e) => {
                list.setPageSize(Number.parseInt(e.target.value, 10));
              }}
            />
          </>
        )}
      </Section>
    </Stack>
  );
}

/**
 * A new credit note against an issued invoice (`/customer-credit-notes/new?invoice=…`;
 * FRD-FIN-060, FRD-VAT-029): its own date, tax date and reason, and lines naming the revenue
 * adjustment account and, optionally, the invoice line they credit.
 */
export function NewCustomerCreditNotePage() {
  const [params] = useSearchParams();
  const invoiceId = params.get('invoice') ?? '';
  const session = useSession();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const today = businessToday(session.data?.businessTimezone);
  const invoice = useQuery({
    queryKey: ['customer-invoice', invoiceId],
    enabled: invoiceId !== '',
    queryFn: () => getData(`${CUSTOMER_INVOICES}/${invoiceId}`, CustomerInvoiceDetailSchema),
  });
  const [creditNoteDate, setDate] = useState(today);
  const [taxDate, setTaxDate] = useState(today);
  const [reason, setReason] = useState('');
  const [lines, setLines] = useState<LineDraft[]>([emptyLine(1)]);
  const [creditedLine, setCreditedLine] = useState<Record<number, string>>({});
  const key = useRef(newIdempotencyKey());
  const create = useMutation({
    mutationFn: () =>
      postJson(
        CREDIT_NOTES,
        {
          customerInvoiceId: invoiceId,
          creditNoteDate,
          taxDate,
          reason: reason.trim(),
          lines: lineRequests(lines, true).map((line, index) => {
            const credited = creditedLine[lines[index]?.key ?? -1] ?? '';
            return credited === '' ? line : { ...line, creditedInvoiceLineId: credited };
          }),
        },
        CustomerCreditNoteDetailSchema,
        session.data?.csrfToken ?? '',
        { idempotencyKey: key.current },
      ),
    onSuccess: async (saved) => {
      await refreshAfterPosting(queryClient);
      await navigate(`/customer-credit-notes/${saved.creditNote.id}`);
    },
  });
  const back = (
    <Button
      component={RouterLink}
      to={invoiceId === '' ? '/customer-credit-notes' : `/customer-invoices/${invoiceId}`}
      startIcon={<ArrowBackIcon />}
    >
      Back
    </Button>
  );
  if (invoiceId === '') {
    return (
      <Stack spacing={3}>
        <PageHeader title="New credit note" actions={back} />
        <Section>
          <EmptyState
            title="Start from the invoice"
            detail="Open the issued invoice to credit and choose Credit note."
          />
        </Section>
      </Stack>
    );
  }
  if (invoice.isPending) return <LoadingState label="Loading the invoice" />;
  if (invoice.isError) {
    return (
      <RecordFailure title="New credit note" noun="invoice" error={invoice.error} back={back} />
    );
  }
  const problems = [
    ...(reason.trim() === '' ? ['Give the reason for the credit.'] : []),
    ...lineProblems(lines, true),
  ];
  return (
    <Stack spacing={3}>
      <PageHeader
        title={`Credit note for ${invoice.data.invoice.documentNo ?? ''}`}
        description={`Outstanding on the invoice: ${invoice.data.invoice.outstandingAmount}`}
        actions={back}
      />
      <Section title="Credit note">
        <Stack spacing={2} sx={{ p: 2 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              type="date"
              label="Credit note date"
              value={creditNoteDate}
              slotProps={{ inputLabel: { shrink: true } }}
              onChange={(e) => {
                setDate(e.target.value);
              }}
            />
            <TextField
              type="date"
              label="Tax date"
              value={taxDate}
              slotProps={{ inputLabel: { shrink: true } }}
              onChange={(e) => {
                setTaxDate(e.target.value);
              }}
            />
          </Stack>
          <TextField
            label="Reason"
            required
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
            }}
          />
        </Stack>
      </Section>
      <Section title="Lines">
        <Stack spacing={2} sx={{ p: 2 }}>
          {lines.map((line, index) => (
            <TextField
              key={line.key}
              select
              size="small"
              label={`Line ${String(index + 1)} credits invoice line`}
              value={creditedLine[line.key] ?? ''}
              helperText="Its VAT is then recorded as an adjustment of that line's VAT (FRD-VAT-029)."
              onChange={(e) => {
                setCreditedLine((current) => ({ ...current, [line.key]: e.target.value }));
              }}
            >
              <MenuItem value="">None (general credit)</MenuItem>
              {invoice.data.lines.map((source) => (
                <MenuItem key={source.id} value={source.id}>
                  Line {source.lineNo} — {source.description} — {source.netAmount}
                </MenuItem>
              ))}
            </TextField>
          ))}
          <LinesEditor lines={lines} onChange={setLines} clientFromHeader />
          <Box>Net total {sum(lines.map((l) => l.netAmount))}</Box>
        </Stack>
      </Section>
      {problems.length > 0 && (
        <Alert severity="info" variant="outlined">
          <ul style={{ margin: 0, paddingInlineStart: '1.2em' }}>
            {problems.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        </Alert>
      )}
      {create.isError && <RefusalState error={create.error} />}
      <Box>
        <Button
          variant="contained"
          disabled={!linesComplete(lines) || problems.length > 0 || create.isPending}
          onClick={() => {
            create.mutate();
          }}
        >
          Save draft
        </Button>
      </Box>
    </Stack>
  );
}

/** One customer credit note (`/customer-credit-notes/:id`) with Issue on a draft. */
export function CustomerCreditNoteDetailPage() {
  const { id = '' } = useParams();
  const session = useSession();
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const detail = useQuery({
    queryKey: ['customer-credit-note', id],
    queryFn: () => getData(`${CREDIT_NOTES}/${id}`, CustomerCreditNoteDetailSchema),
  });
  const issue = useMutation({
    mutationFn: (version: number) =>
      postJson(
        `${CREDIT_NOTES}/${id}/issue`,
        {},
        CustomerCreditNoteDetailSchema,
        session.data?.csrfToken ?? '',
        {
          expectedVersion: version,
        },
      ),
    onSuccess: async () => {
      setConfirming(false);
      await refreshAfterPosting(queryClient);
      await queryClient.invalidateQueries({ queryKey: ['customer-credit-note', id] });
    },
  });
  const back = (
    <Button component={RouterLink} to="/customer-credit-notes" startIcon={<ArrowBackIcon />}>
      All credit notes
    </Button>
  );
  if (detail.isPending) return <LoadingState label="Loading the credit note" />;
  if (detail.isError) {
    return (
      <RecordFailure
        title="Customer credit note"
        noun="credit note"
        error={detail.error}
        back={back}
      />
    );
  }
  const { creditNote, journal, lines } = detail.data;
  const canIssue = creditNote.status === 'draft' && hasCapability(session.data, `${NOTE}:post`);
  return (
    <Stack spacing={3}>
      <PageHeader
        title={`Credit note ${creditNote.documentNo ?? `draft #${creditNote.id}`}`}
        description={creditNote.clientLabel ?? 'Customer credit note'}
        actions={
          <>
            {back}
            {canIssue && (
              <Button
                variant="contained"
                onClick={(event) => {
                  event.currentTarget.focus();
                  setConfirming(true);
                }}
              >
                Issue
              </Button>
            )}
          </>
        }
      />
      {issue.isError && <RefusalState error={issue.error} />}
      <Section title="Credit note">
        <Facts
          label="Credit note details"
          facts={
            [
              ['Status', <StatusChip key="s" status={creditNote.status} />],
              [
                'Credits invoice',
                <RecordLink key="i" to={`/customer-invoices/${creditNote.customerInvoiceId}`}>
                  {creditNote.invoiceDocumentNo ?? creditNote.customerInvoiceId}
                </RecordLink>,
              ],
              ['Date', creditNote.creditNoteDate],
              ['Tax date', creditNote.taxDate],
              ['Reason', creditNote.reason],
              ['Net', creditNote.netTotal],
              [
                'VAT',
                `${creditNote.vatTotal}${creditNote.vatCharged ? ' (adjusts charged VAT)' : ''}`,
              ],
              ['Total', creditNote.grossTotal],
              ['Journal', <JournalRef key="j" journal={journal} />],
            ] as [string, ReactNode][]
          }
        />
      </Section>
      <Section title="Lines">
        <Table size="small" aria-label="Credit note lines">
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Account</TableCell>
              <TableCell>Credits line</TableCell>
              <TableCell align="right">Net</TableCell>
              <TableCell align="right">VAT</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {lines.map((line) => (
              <TableRow key={line.id}>
                <TableCell>{line.lineNo}</TableCell>
                <TableCell>{line.description}</TableCell>
                <TableCell>
                  {line.accountCode === null
                    ? '—'
                    : `${line.accountCode} — ${line.accountName ?? ''}`}
                </TableCell>
                <TableCell>
                  {line.creditedInvoiceLine === null
                    ? 'General'
                    : `Line ${String(line.creditedInvoiceLine.lineNo)}`}
                </TableCell>
                <TableCell align="right">{line.netAmount}</TableCell>
                <TableCell align="right">{line.vat?.vatAmount ?? '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Section>
      <DocumentsPanel
        recordType={NOTE}
        recordId={creditNote.id}
        canAttach={hasCapability(session.data, `${NOTE}:create`)}
      />
      <RecordHistory recordType={NOTE} recordId={creditNote.id} />
      {confirming && (
        <ConfirmDialog
          open
          title="Issue the credit note?"
          message="It reduces what the client owes on the invoice and is never changed afterwards."
          confirmLabel="Issue"
          busy={issue.isPending}
          error={issue.isError ? issue.error.message : null}
          onCancel={() => {
            setConfirming(false);
          }}
          onConfirm={() => {
            issue.mutate(creditNote.version);
          }}
        />
      )}
    </Stack>
  );
}
